import { useCallback } from 'react';
import {
    AITask,
    AIRequest,
    AIResponse,
    AITaskRequestContext,
    AIError,
    GetTaskOptions
} from '~/packages/ai-core';
import { useHoneybookAI } from './honeybook-ai-provider';
import { DoneResponseData, IHBError } from '~/packages/ai-core';

export interface UseAITaskOptions {
    feature: string;
    onResponse?: (response: AIResponse) => void;
    onError?: (error: IHBError | AIError) => void;
    onComplete?: (completionData: DoneResponseData) => void;
    // Initial timeout for the task responses
    timeout?: number;
    context?: AITaskRequestContext;
}

interface UseAITaskResult {
    startTask: (
        requests: AIRequest[],
        options?: { taskKey?: string }
    ) => Promise<AITask>;
    resumeTask: (options: GetTaskOptions) => Promise<AITask | null>;
}

export const useAITask = ({
    feature,
    onResponse,
    onError,
    onComplete,
    timeout,
    context
}: UseAITaskOptions): UseAITaskResult => {
    const ai = useHoneybookAI();

    // Shared response processing logic for both startTask and getTask
    const processTaskResponses = useCallback(
        (task: AITask) => {
            // Process responses asynchronously
            (async () => {
                try {
                    for await (const response of task.responseStream()) {
                        if (response.type === 'error') {
                            onError?.(response.content as IHBError);
                        } else if (response.type === 'done') {
                            onComplete?.(response.content as DoneResponseData);
                        } else {
                            onResponse?.(response);
                        }
                    }
                } catch (e) {
                    onError?.(e as AIError);
                }
            })();
        },
        [onResponse, onError, onComplete]
    );

    // Always creates a new task when called.
    const startTask = useCallback(
        async (
            requests: AIRequest[],
            options?: { taskKey?: string }
        ): Promise<AITask> => {
            // Let any errors during task creation propagate.
            const taskRepo = ai.getTaskRepository();
            const createOptions = { timeout, ...options };
            const newTask = await taskRepo.createTask(
                feature,
                requests,
                createOptions,
                context
            );

            // Set up response processing for the new task
            processTaskResponses(newTask);

            return newTask;
        },
        [ai, feature, timeout, context, processTaskResponses]
    );

    // Resume an existing task by taskId or taskKey and automatically set up response processing
    const resumeTask = useCallback(
        async (options: GetTaskOptions): Promise<AITask | null> => {
            const taskRepo = ai.getTaskRepository();
            const retrievedTask = await taskRepo.getTask(feature, options);

            if (retrievedTask) {
                // Set up response processing for the retrieved task (resume functionality)
                processTaskResponses(retrievedTask);
            }

            return retrievedTask;
        },
        [ai, feature, processTaskResponses]
    );

    return {
        startTask,
        resumeTask
    };
};

export default useAITask;
